import { DistributorParams } from '@/api/distributor.api';
import {
  cancelPartnershipRequest,
  findPartnershipRequests,
  getAllReceivers,
  getPartnerReceiverInfo,
  getReceivers,
  PartnershipRequestsParam,
  ReceiversParam,
  ReceiversResponse,
  ReceiversToAddResponse,
  removePartnerCompany,
  sendPartnershipRequest,
} from '@/api/receiver.api';
import { queryKeys } from '@/queryKeys';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export const useGetMyReceivers = <TData = ReceiversResponse>(
  params: DistributorParams,
  select?: (data: ReceiversResponse) => TData,
) =>
  useQuery({
    queryKey: queryKeys.receivers.list(params),
    queryFn: () => getReceivers(params),
    placeholderData: keepPreviousData,
    select,
  });

export const useGetAllReceivers = <TData = ReceiversToAddResponse>(
  params: ReceiversParam,
  select?: (data: ReceiversToAddResponse) => TData,
) =>
  useQuery({
    queryKey: queryKeys.receivers.allList(params),
    queryFn: () => getAllReceivers(params),
    placeholderData: keepPreviousData,
    select,
  });

export const useSendPartnershipRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (receivers: string[]) => sendPartnershipRequest(receivers),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.receivers.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.receivers.allListBase,
      });
    },
  });
};

export const useFindPartnershipRequests = (params: PartnershipRequestsParam) =>
  useQuery({
    queryKey: queryKeys.receivers.partnershipRequests(params),
    queryFn: () => findPartnershipRequests(params),
    placeholderData: keepPreviousData,
  });

export const useCancelPartnershipRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (receiverId: string) => cancelPartnershipRequest(receiverId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.receivers.all });
    },
  });
};

export const useGetPartnerReceiverInfo = (receiverId: string) =>
  useQuery({
    queryKey: queryKeys.receivers.partnerInfo(receiverId),
    queryFn: () => getPartnerReceiverInfo(receiverId),
  });

export const useRemovePartnerCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (receiverId: string) => removePartnerCompany(receiverId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.receivers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.circuits.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.distributors.circuits,
      });
    },
  });
};
